import React, { useEffect, useMemo } from 'react';

import { CssBaseline, GlobalStyles } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LicenseInfo } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import es from 'dayjs/locale/es';
import isBetween from 'dayjs/plugin/isBetween';
import 'overlayscrollbars/overlayscrollbars.css';

import { ToastContainer } from '@/components/alerts/ToastContainer';
import { ENV } from '@/constants/env';
import { useLocaleStore } from '@/features/pageContainer/stores/useLocaleStore';
import i18n from '@/i18n';
import { theme } from '@/theme';
import { GLOBAL_STYLES } from '@/theme/overrides/global-styles';

import { AppRouter } from './AppRouter';

dayjs.extend(isBetween);
dayjs.locale({ ...es, weekStart: 1 });
dayjs.locale(i18n.language.split('-').shift());

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
});

LicenseInfo.setLicenseKey(ENV.LICENSE_MUI_XPRO);

export const App = () => {
    const { locale } = useLocaleStore();

    useEffect(() => {
        if (!locale) return;

        i18n.changeLanguage(locale);
    }, [locale]);

    const adapterLocale = useMemo(() => i18n.language.split('-').shift(), []);

    return (
        <ThemeProvider theme={theme}>
            <StyledEngineProvider injectFirst>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                    <CssBaseline />
                    <GlobalStyles styles={GLOBAL_STYLES} />
                    <QueryClientProvider client={queryClient}>
                        <AppRouter />
                        <ToastContainer />
                        <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                </LocalizationProvider>
            </StyledEngineProvider>
        </ThemeProvider>
    );
};

export default App;
